import { compact } from '@leland-dev/leland-ui-library';
import { useCallback, useState } from 'react';

import { type Scalars } from '../../__generated-gql-types__/globalTypes';
import { useOnce } from '../../utils/hooks/useOnce';
import { wasMutationSuccessful } from '../../utils/mutations';

import {
  type SchoolFiltersSchoolFragment,
  useSchoolFiltersGetSchoolsLazyQuery,
} from './__generated-gql-types__/SchoolFilters.generated';

interface UseSchoolFiltersProps {
  initialCache?: Record<string, SchoolFiltersSchoolFragment>;
  initialSelectedSchoolIds?: Array<Scalars['SchoolUrn']['output']>;
  onChange?: (
    schools: SchoolFiltersSchoolFragment[],
    preventScroll?: boolean,
  ) => void;
}

interface SchoolObjects {
  schools: SchoolFiltersSchoolFragment[];
}

interface SchoolIds {
  schoolIds: Array<Scalars['SchoolUrn']['output']>;
}

type SchoolObjectsOrIds = SchoolObjects | SchoolIds;

export interface SchoolFilterHookOutput {
  addSchoolFilters: (
    schoolsOrIds: SchoolObjectsOrIds,
    preventScroll?: boolean,
  ) => Promise<void>;
  removeSchoolFilters: (schoolIds: string[], preventScroll?: boolean) => void;
  selectedSchools: SchoolFiltersSchoolFragment[];
}

export const useSchoolFilters = ({
  initialCache,
  initialSelectedSchoolIds,
  onChange,
}: UseSchoolFiltersProps = {}): SchoolFilterHookOutput => {
  const [selectedSchoolsCache, setSelectedSchoolsCache] = useState<
    Record<string, SchoolFiltersSchoolFragment>
  >(initialCache ?? {});
  const [selectedSchoolsMap, setSelectedSchoolsMap] = useState<
    Record<string, boolean>
  >({});
  // decorate selected schools if not yet in cache
  const [lazyGetSchools] = useSchoolFiltersGetSchoolsLazyQuery();

  const getSelectedSchools = useCallback(
    (
      selectedSchoolCache: Record<string, SchoolFiltersSchoolFragment>,
      selectedSchoolMap: Record<string, boolean>,
    ) => {
      const results: SchoolFiltersSchoolFragment[] = [];
      for (const school in selectedSchoolMap) {
        if (selectedSchoolMap[school]) {
          results.push(selectedSchoolCache[school]);
        }
      }
      return results;
    },
    [],
  );

  const addSchoolFilters = useCallback(
    async (schools: SchoolObjectsOrIds, preventScroll?: boolean) => {
      const newSelectedSchoolsCache = { ...selectedSchoolsCache };

      const schoolsToAdd: Array<Possible<SchoolFiltersSchoolFragment>> = [];
      if ('schools' in schools) {
        schoolsToAdd.push(...schools.schools);
      } else if ('schoolIds' in schools) {
        const schoolsToFetch = schools.schoolIds.filter(
          (schoolId) => !newSelectedSchoolsCache[schoolId],
        );
        const res = await lazyGetSchools({
          variables: {
            ids: schoolsToFetch,
          },
        });
        if (!wasMutationSuccessful(res)) console.warn(res.error);
        for (const school of res.data?.schoolsByIds ?? []) {
          newSelectedSchoolsCache[school.id] = school;
        }
        schoolsToAdd.push(
          ...schools.schoolIds
            .map((id) => newSelectedSchoolsCache[id])
            .filter(compact),
        );
      }

      const newSelectedSchoolsMap = { ...selectedSchoolsMap };

      for (const school of schoolsToAdd) {
        if (!school) return;
        if (newSelectedSchoolsMap[school.id]) return;
        if (newSelectedSchoolsMap[school.id] === undefined) {
          newSelectedSchoolsCache[school.id] = school;
        }
        newSelectedSchoolsMap[school.id] = true;
      }

      setSelectedSchoolsCache(newSelectedSchoolsCache);
      setSelectedSchoolsMap(newSelectedSchoolsMap);
      onChange?.(
        getSelectedSchools(newSelectedSchoolsCache, newSelectedSchoolsMap),
        preventScroll,
      );
    },
    [
      selectedSchoolsMap,
      selectedSchoolsCache,
      onChange,
      getSelectedSchools,
      lazyGetSchools,
    ],
  );

  const removeSchoolFilters = useCallback(
    (schoolIds: string[], preventScroll?: boolean) => {
      const newSelectedSchoolsMap = { ...selectedSchoolsMap };
      for (const schoolId of schoolIds) {
        newSelectedSchoolsMap[schoolId] = false;
      }
      setSelectedSchoolsMap(newSelectedSchoolsMap);
      onChange?.(
        getSelectedSchools(selectedSchoolsCache, newSelectedSchoolsMap),
        preventScroll,
      );
    },
    [getSelectedSchools, onChange, selectedSchoolsMap, selectedSchoolsCache],
  );

  useOnce(() => {
    if (initialSelectedSchoolIds?.length) {
      void addSchoolFilters({ schoolIds: initialSelectedSchoolIds });
    }
  }, !!initialSelectedSchoolIds);

  return {
    addSchoolFilters,
    removeSchoolFilters,
    selectedSchools: getSelectedSchools(
      selectedSchoolsCache,
      selectedSchoolsMap,
    ),
  };
};
